import {NextPage} from "next";
import {StoreView} from "../../components/store/StoreView";
import {StoreJson, StoreModel} from "../../models/store";
import {wrapper} from "../../app/store";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import Bugsnag from "@bugsnag/js";
import React from "react";
import {ImageUtils} from "../../utils/imageUtils";
import {MetaTag} from "../../models/metaTag";

interface StoreProps {
  id: string
  storeJson?: StoreJson
  metaTags: MetaTag[]
}

const Store: NextPage<StoreProps> = ({ id, storeJson }) => {
  const store = StoreModel.fromJson(storeJson)

  return (
    <>
      <StoreView storeId={id} store={store} />
    </>
  )
}

const createMetaTags = (params: any, storeJson?: StoreJson): MetaTag[] => {
  const store = StoreModel.fromJson(storeJson)

  const metaTitle = (store && `${store.header?.title ?? 'FitGenie'} | Order ${storeJson?.distribution_methods?.includes('delivery') ? 'Delivery' : 'Pickup'} on FitGenie`) ?? null
  const metaDescription = (store && (store?.header?.subtitle ?? `Order ${storeJson?.distribution_methods?.includes('delivery') ? 'Delivery' : 'Pickup'} From ${store?.header?.title} on FitGenie`)) ?? null
  const metaImage = ImageUtils.createUrlFromImage(store?.header?.coverImage) ?? null
  const metaUrl = `${process.env.NEXT_PUBLIC_APP_DOMAIN_URL}/store/${params.id ?? store?.id}`

  return [
    {
      property: 'og:title',
      content: metaTitle
    },
    {
      property: 'og:description',
      content: metaDescription
    },
    {
      property: 'og:image',
      content: metaImage
    },
    {
      property: 'og:url',
      content: metaUrl
    },
    {
      property: 'twitter:title',
      content: metaTitle
    },
    {
      property: 'twitter:description',
      content: metaDescription
    },
    {
      property: 'twitter:image',
      content: metaImage
    },
    {
      property: 'twitter:card',
      content: 'summary_large_image'
    },
  ]
}

export async function getStaticPaths() {
  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-available-stores`).catch(e => {
    Bugsnag.notify(e)
    return undefined
  })
  const stores: StoreJson[] = (await res?.json()) ?? []

  const paths = stores.map((store) => ({
    params: { id: store._id },
  }))

  return { paths, fallback: true }
}

export const getStaticProps = wrapper.getStaticProps(store => async ({ locale, params }: any) => {
  const translations = await serverSideTranslations(locale, ['common', 'landing', 'auth', 'location', 'store', 'payment', 'checkout', 'order'])
    .catch(e => ({}))

  const res = await fetch(`${process.env.NEXT_PUBLIC_BASE_URL}/store/get-store/${params.id}`).catch(e => {
    Bugsnag.notify(e)
    return undefined
  })

  const storeJson: StoreJson | undefined = await res?.json()

  // Pass post data to the page via props
  return {
    props: {
      id: params.id,
      storeJson: storeJson,
      metaTags: createMetaTags(params, storeJson),
      ...translations,
    }
  }
});

export default Store